import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useServicesStatus } from '../../customHooks/useServicesStatus';
import '../../assets/stylesheets/HealthCheck.css';

export const HealthCheck = () => {

  const SERVICES = [
    { key: 'backend', label: 'Backend' },
    { key: 'mongodb', label: 'MongoDB' },
    { key: 'postgresql', label: 'PostgreSQL' },
  ];

  // Verificar si el usuario tiene permisos para ver el menú de health check
  const currentUser = useSelector(state => state.currentUser);
  const hasHealthCheckAccess = currentUser && (currentUser.isAdmin || currentUser.isExamAdmin);
 
  // Estado del menú de health check
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isAuthenticated = !!currentUser;

  const {
    servicesStatus,
    isLoading
  } = useServicesStatus(isAuthenticated);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getOverallStatus = () => {
    const serviceStatuses = SERVICES.map(service => servicesStatus[service.key]);
    const allOnline = serviceStatuses.every(status => status === 'online');

    return {
      color: allOnline ? 'green' : 'red',
      emoji: allOnline ? '🟢' : '🔴'
    };
  };

  // obtener el estado de un servicio específico
  const getServiceStatus = (service) => {
    const status = servicesStatus[service];
    if (status === 'online') return { emoji: '🟢', className: 'status-online', text: 'Online' };
    if (status === 'offline') return { emoji: '🔴', className: 'status-offline', text: 'Offline' };
    return { emoji: '🟡', className: 'status-checking', text: 'Verificando...' };
  };

  // Obtener servicios offline para mostrar en estado general
  const getOfflineServices = () => {
    const offlineServices = [];
    SERVICES.forEach(service => {
      if (servicesStatus[service.key] === 'offline') offlineServices.push(service.label);
    });
    return offlineServices;
  };

  // Generar mensaje de estado general
  const getGeneralStatusMessage = () => {
    const offlineServices = getOfflineServices();

    if (Object.values(servicesStatus).some(status => status === 'Verificando...')) {
      return {
        type: 'checking',
        message: 'Verificando el estado de los servicios...',
        icon: '🔄'
      };
    }

    if (offlineServices.length === 0) {
      return {
        type: 'success',
        message: 'Todos los servicios están funcionando correctamente',
        icon: '✅'
      };
    } else if (offlineServices.length === 1) {
      return {
        type: 'warning',
        message: `El servicio de ${offlineServices[0]} está presentando inconvenientes. Por favor, informe este suceso al administrador del sistema.`,
        icon: '⚠️'
      };
    } else {
      const servicesList = offlineServices.slice(0, -1).join(', ') + ' y ' + offlineServices[offlineServices.length - 1];
      return {
        type: 'error',
        message: `Los servicios de ${servicesList} están presentando inconvenientes. Por favor, informe este suceso al administrador del sistema.`,
        icon: '🚨'
      };
    }
  };

  const generalStatus = getGeneralStatusMessage();
  const overallStatus = getOverallStatus();

  const renderServices = () => {
    return SERVICES.map(service => {
      const status = getServiceStatus(service.key);
      return (
        <div key={service.key} className="health-check-service">
          <span className="service-name">{service.label}</span>
          <span className={`service-status ${status.className}`}>
            {status.emoji} {status.text}
          </span>
        </div>
      );
    });
  };

  return (
    <div className="health-check-container">
      <span
        onClick={toggleMenu}
        className="health-check-indicator"
        style={{ color: overallStatus.color }}
      >
        {overallStatus.emoji} Estado del sistema
      </span>
      {isMenuOpen && hasHealthCheckAccess && (
        <div className="health-check-menu">
          {/* Sección: Estado General */}
          <div className="health-check-section">
            <div className="section-title">Estado general</div>
            <div className={`general-status-message ${generalStatus.type}`}>
              <span className="status-icon">{generalStatus.icon}</span>
              <span className="status-text">{generalStatus.message}</span>
            </div>
          </div>
          {/* Sección: Servicios */}
          <div className="health-check-section">
            <div className="section-title">Servicios</div>
            <div className="services-list">
              {renderServices()}
            </div>
          </div>
          {/* Sección: Usuarios Activos */}
          <div className="health-check-section">
            <div className="section-title">Usuarios activos</div>
            <div className="active-users-count">
              <span className="active-users-number">{servicesStatus.activeUsers}</span>
              <span className="active-users-label">en línea</span>
            </div>
          </div>
          <div className="health-check-timestamp">
            Última actualización: {servicesStatus.timestamp || 'N/A'}
          </div>
        </div>
      )}
      {isMenuOpen && hasHealthCheckAccess && (
        <div 
          className="health-check-overlay"
          onClick={closeMenu}
        />
      )}
    </div>
  );
};